import React, { useEffect, useState, useRef, Fragment } from 'react';
import { motion } from 'framer-motion';
import { SparklesIcon, MicIcon, ImageIcon, PaperclipIcon, ArrowUpIcon, CarIcon, HomeIcon, BriefcaseIcon, TagIcon, TruckIcon, SmartphoneIcon, HistoryIcon, ChevronRightIcon, XIcon, PlusIcon, TrendingDownIcon, ThumbsUpIcon, AlertTriangleIcon, WrenchIcon, ShoppingBagIcon, GraduationCapIcon, PlaneIcon, UserIcon } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { AIResponse } from '../components/AIResponse';
import { ListingCard, ListingCardProps } from '../components/ListingCard';
import { ListingCarousel } from '../components/ListingCarousel';
import { useBusiness, BusinessType } from '../components/BusinessContext';
import { useAuth } from '../components/AuthContext';
// --- Seller intent detection ---
type SellIntent = {
  tier: 'individual' | 'business' | 'enterprise';
  type: BusinessType;
};
const detectSellIntent = (text: string): SellIntent | null => {
  const t = text.toLowerCase();
  const sellish = /\b(sell|selling|list|listing|post|inventory|stock|dealer|agency|recruit|hire|hiring|jobs?|shop|store|business|merchant|products?|services?|manage (leads|inquiries|properties|listings))\b/.test(t);
  if (!sellish) return null;
  // Business type
  let type: BusinessType = 'generic';
  if (/\b(car|cars|vehicle|vehicles|auto|motors?|dealership|dealer)\b/.test(t)) type = 'car';else if (/\b(real estate|property|properties|villa|apartment|agency|broker)\b/.test(t)) type = 'realestate';else if (/\b(recruit|hiring|hire|jobs?|candidates?|talent)\b/.test(t)) type = 'recruitment';else if (/\b(service|services|provider|cleaning|moving|maintenance)\b/.test(t)) type = 'service';else if (/\b(products?|furniture|electronics|goods|items?|inventory|shop|store)\b/.test(t)) type = 'product';
  // Enterprise signals: dealer / agency / recruiter / company / multiple
  const enterprise = /\b(dealer|dealership|agency|recruiter|recruitment|company|enterprise|fleet|represent)\b/.test(t);
  // Business signals: ongoing selling, inventory, multiple, manage
  const business = /\b(business|shop|store|inventory|stock|products?|services?|multiple|many|several|bulk|manage (leads|inquiries|listings))\b/.test(t);
  // Individual: "sell my <single item>"
  const individual = /\bsell(ing)?\s+(my|a|an|this|one)\s+\w+/.test(t);
  let tier: SellIntent['tier'] = 'individual';
  if (enterprise) tier = 'enterprise';else if (business) tier = 'business';else if (individual) tier = 'individual';else tier = 'business'; // generic seller phrasing leans business
  return {
    tier,
    type
  };
};
const BUSINESS_LABEL_INLINE: Record<BusinessType, string> = {
  car: 'vehicle inventory',
  realestate: 'property listings',
  recruitment: 'job postings and candidates',
  service: 'services',
  product: 'product inventory',
  generic: 'listings'
};
const suggestedPrompts = [{
  text: 'Find me a Prado under AED 90,000',
  icon: CarIcon
}, {
  text: "Need a villa near my children's school",
  icon: HomeIcon
}, {
  text: 'Help me find a job',
  icon: BriefcaseIcon
}, {
  text: 'Find a moving company tomorrow',
  icon: WrenchIcon
}, {
  text: 'Compare iPhone 16 Pro prices',
  icon: SmartphoneIcon
}, {
  text: 'Find flights to Istanbul',
  icon: PlaneIcon
}];
const categoryRibbonItems = [{
  label: 'Cars',
  icon: CarIcon,
  prompt: 'Find me a car'
}, {
  label: 'Properties',
  icon: HomeIcon,
  prompt: 'Find me a property'
}, {
  label: 'Jobs',
  icon: BriefcaseIcon,
  prompt: 'Help me find a job'
}, {
  label: 'Services',
  icon: WrenchIcon,
  prompt: 'I need a service'
}, {
  label: 'Products',
  icon: ShoppingBagIcon,
  prompt: "I'm looking for a product"
}, {
  label: 'Moving',
  icon: TruckIcon,
  prompt: 'Find me a moving company'
}, {
  label: 'Education',
  icon: GraduationCapIcon,
  prompt: 'Find education or courses'
}, {
  label: 'Travel',
  icon: PlaneIcon,
  prompt: 'Plan my travel'
}, {
  label: 'Professionals',
  icon: UserIcon,
  prompt: 'Find a professional'
}];
const recentSearches = ['Prado under AED 90,000', 'Villa near GEMS School', 'ERP Manager job in Dubai', 'iPhone 16 Pro price'];
// Mock data for search results
const mockInsights = [{
  title: 'Average Price',
  value: 'AED 84,000',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: 'Prado GXR 2018',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: '1'
}, {
  title: 'Lowest Mileage',
  value: 'Prado VX 2019',
  type: 'positive' as const,
  icon: SparklesIcon,
  targetId: '4'
}, {
  title: 'Risk Alerts',
  value: '4 imported detected',
  type: 'warning' as const,
  icon: AlertTriangleIcon
}];
const propertyInsights = [{
  title: 'Average Price',
  value: 'AED 2.8M',
  type: 'neutral' as const,
  icon: TrendingDownIcon
}, {
  title: 'Best Value',
  value: '5BR Villa — Mudon',
  type: 'positive' as const,
  icon: ThumbsUpIcon,
  targetId: 'p3'
}, {
  title: 'Closest to School',
  value: 'Dubai Hills · 3 min',
  type: 'positive' as const,
  icon: HomeIcon,
  targetId: 'p5'
}, {
  title: 'Note',
  value: '2 listings under offer',
  type: 'warning' as const,
  icon: AlertTriangleIcon
}];
type SearchCategory = 'cars' | 'property';
const detectCategory = (text: string): SearchCategory => {
  const t = text.toLowerCase();
  const propertyHints = ['villa', 'apartment', 'apt', 'home', 'house', 'property', 'bedroom', 'br ', 'townhouse', 'flat', 'rent', 'school', 'studio'];
  return propertyHints.some(h => t.includes(h)) ? 'property' : 'cars';
};
const CAR_IMG_A = 'https://images.unsplash.com/photo-1590362891991-f776e747a588?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_B = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?auto=format&fit=crop&q=80&w=800';
const CAR_IMG_C = 'https://images.unsplash.com/photo-1549317661-bd32c8ce0db2?auto=format&fit=crop&q=80&w=800';
// Full result set (label says 27; we show a representative slice in the carousel)
const mockListings = [{
  id: '1',
  title: 'Toyota Prado GXR 2018',
  price: 'AED 82,000',
  location: 'Al Quoz, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '112,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 98,
  sellerType: 'Verified Business' as const
}, {
  id: '2',
  title: 'Toyota Prado TXL 2019',
  price: 'AED 89,000',
  location: 'Abu Shagara, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '85,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 94,
  sellerType: 'Individual' as const
}, {
  id: '3',
  title: 'Toyota Prado VXR 2017',
  price: 'AED 78,000',
  location: 'Motor City, Dubai',
  images: [CAR_IMG_C, CAR_IMG_A],
  specs: [{
    label: 'Year',
    value: '2017'
  }, {
    label: 'Mileage',
    value: '145,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Black'
  }],
  matchScore: 88,
  sellerType: 'Dealer' as const
}, {
  id: '4',
  title: 'Toyota Prado VX 2019',
  price: 'AED 87,500',
  location: 'Mussafah, Abu Dhabi',
  images: [CAR_IMG_A, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2019'
  }, {
    label: 'Mileage',
    value: '64,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 92,
  sellerType: 'Verified Business' as const
}, {
  id: '5',
  title: 'Toyota Prado GXR 2016',
  price: 'AED 69,000',
  location: 'Al Nahda, Sharjah',
  images: [CAR_IMG_B, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2016'
  }, {
    label: 'Mileage',
    value: '168,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Grey'
  }],
  matchScore: 81,
  sellerType: 'Individual' as const
}, {
  id: '6',
  title: 'Toyota Prado TXL 2018',
  price: 'AED 84,900',
  location: 'Deira, Dubai',
  images: [CAR_IMG_C, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '98,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'White'
  }],
  matchScore: 90,
  sellerType: 'Dealer' as const
}, {
  id: '7',
  title: 'Toyota Prado VX 2018',
  price: 'AED 86,000',
  location: 'JVC, Dubai',
  images: [CAR_IMG_A, CAR_IMG_B],
  specs: [{
    label: 'Year',
    value: '2018'
  }, {
    label: 'Mileage',
    value: '77,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Beige'
  }],
  matchScore: 89,
  sellerType: 'Verified Business' as const
}, {
  id: '8',
  title: 'Toyota Prado GXR 2017',
  price: 'AED 74,500',
  location: 'Al Ain',
  images: [CAR_IMG_B, CAR_IMG_C],
  specs: [{
    label: 'Year',
    value: '2017'
  }, {
    label: 'Mileage',
    value: '132,000 km'
  }, {
    label: 'Specs',
    value: 'GCC'
  }, {
    label: 'Color',
    value: 'Silver'
  }],
  matchScore: 83,
  sellerType: 'Individual' as const
}];
// The 3 strong matches after the user narrows their request
const narrowedListings = [mockListings[3], mockListings[6], mockListings[0]];
// ---- Real-estate (property) dataset ----
const HOME_IMG_A = 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_B = 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&q=80&w=800';
const HOME_IMG_C = 'https://images.unsplash.com/photo-1512917774080-9991f1c4c750?auto=format&fit=crop&q=80&w=800';
const propertyListings: ListingCardProps[] = [{
  id: 'p1',
  title: '4BR Villa — Arabian Ranches',
  price: 'AED 3.2M',
  location: 'Arabian Ranches, Dubai',
  images: [HOME_IMG_A, HOME_IMG_C],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,400 sqft'
  }, {
    label: 'School',
    value: '5 min'
  }],
  matchScore: 97,
  sellerType: 'Verified Business' as const
}, {
  id: 'p2',
  title: '3BR Townhouse — Town Square',
  price: 'AED 1.9M',
  location: 'Town Square, Dubai',
  images: [HOME_IMG_B, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,100 sqft'
  }, {
    label: 'School',
    value: '8 min'
  }],
  matchScore: 93,
  sellerType: 'Dealer' as const
}, {
  id: 'p3',
  title: '5BR Villa — Mudon',
  price: 'AED 4.1M',
  location: 'Mudon, Dubai',
  images: [HOME_IMG_C, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '5 BR'
  }, {
    label: 'Bathrooms',
    value: '6 Bath'
  }, {
    label: 'Area',
    value: '4,200 sqft'
  }, {
    label: 'School',
    value: '4 min'
  }],
  matchScore: 95,
  sellerType: 'Verified Business' as const
}, {
  id: 'p4',
  title: '4BR Villa — The Springs',
  price: 'AED 2.8M',
  location: 'The Springs, Dubai',
  images: [HOME_IMG_A, HOME_IMG_B],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '4 Bath'
  }, {
    label: 'Area',
    value: '2,900 sqft'
  }, {
    label: 'School',
    value: '6 min'
  }],
  matchScore: 91,
  sellerType: 'Individual' as const
}, {
  id: 'p5',
  title: '3BR Apartment — Dubai Hills',
  price: 'AED 2.3M',
  location: 'Dubai Hills Estate',
  images: [HOME_IMG_C, HOME_IMG_A],
  specs: [{
    label: 'Bedrooms',
    value: '3 BR'
  }, {
    label: 'Bathrooms',
    value: '3 Bath'
  }, {
    label: 'Area',
    value: '1,800 sqft'
  }, {
    label: 'School',
    value: '3 min'
  }],
  matchScore: 89,
  sellerType: 'Dealer' as const
}, {
  id: 'p6',
  title: '4BR Villa — Reem Community',
  price: 'AED 2.6M',
  location: 'Reem, Dubai',
  images: [HOME_IMG_B, HOME_IMG_C],
  specs: [{
    label: 'Bedrooms',
    value: '4 BR'
  }, {
    label: 'Bathrooms',
    value: '5 Bath'
  }, {
    label: 'Area',
    value: '3,100 sqft'
  }, {
    label: 'School',
    value: '7 min'
  }],
  matchScore: 88,
  sellerType: 'Verified Business' as const
}];
const propertyNarrowed = [propertyListings[2], propertyListings[0], propertyListings[4]];
type SelectedVehicle = {
  id: string;
  title: string;
};
type ChatMessage = {
  role: 'user' | 'ai';
  content: string;
  // 'search' = first broad result set (carousel)
  // 'narrow_prompt' = follow-up offer to refine the results
  // 'narrowed' = refined 3-result set
  // 'vehicle_answer' = AI answer about specific selected vehicles
  kind?: 'search' | 'narrow_prompt' | 'narrowed' | 'vehicle_answer' | 'seller_offer' | 'seller_simple';
  vehicles?: SelectedVehicle[];
  category?: SearchCategory;
  sellType?: BusinessType;
};
export function Discover() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    activateHub,
    hub
  } = useBusiness();
  const {
    requireAuth
  } = useAuth();
  const isShopMode = !!slug;
  const shopLogo = isShopMode && hub?.shopSlug === slug ? hub.logo : null;
  const shopName = isShopMode && hub?.shopSlug === slug ? hub.name : null;
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const [hasNarrowed, setHasNarrowed] = useState(false);
  const [searchCategory, setSearchCategory] = useState<SearchCategory>('cars');
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [selectedVehicles, setSelectedVehicles] = useState<SelectedVehicle[]>([]);
  const bottomRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const docInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [attachments, setAttachments] = useState<string[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const handleFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    const names = Array.from(files).map(f => f.name);
    setAttachments(prev => [...prev, ...names]);
    e.target.value = '';
  };
  const removeAttachment = (idx: number) => setAttachments(prev => prev.filter((_, i) => i !== idx));
  const toggleRecording = () => setIsRecording(r => !r);
  // Keep the latest message + input in view as the conversation grows.
  // Scroll the CONTAINER to its full height (more reliable than scrollIntoView
  // when AI replies add tall content/images that grow height after paint).
  useEffect(() => {
    const scrollToBottom = () => {
      const el = scrollContainerRef.current;
      if (el) el.scrollTo({
        top: el.scrollHeight,
        behavior: 'smooth'
      });
    };
    const raf = requestAnimationFrame(scrollToBottom);
    const t1 = setTimeout(scrollToBottom, 250);
    const t2 = setTimeout(scrollToBottom, 700);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [chatHistory, isSearching]);
  const scrollToListing = (id: string) => {
    const el = document.getElementById(`listing-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest'
      });
      el.classList.add('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-primary-indigo', 'ring-offset-2');
      }, 1600);
    }
  };
  const resetConversation = () => {
    setChatHistory([]);
    setHasResults(false);
    setHasNarrowed(false);
    setSelectedVehicles([]);
    setSearchCategory('cars');
    setQuery('');
    setIsSearching(false);
  };
  // Clicking "Discover" in the nav navigates to "/" with a fresh `reset`
  // timestamp — start a brand-new chat even if already on this route.
  useEffect(() => {
    if (location.state && (location.state as {
      reset?: number;
    }).reset) {
      resetConversation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  // Business seller accepted — require sign-in, then create the workspace
  // and take the user straight into the Business Hub.
  const createBusinessHub = (type: BusinessType) => {
    requireAuth(() => {
      activateHub(type);
      navigate('/business');
    });
  };
  // Business seller declined — fall back to a single listing in chat
  const continueWithOneListing = () => {
    setChatHistory(prev => [...prev, {
      role: 'ai',
      kind: 'seller_simple',
      content: "No problem — let's create a single listing right here. Upload a few photos and tell me the key details, and I'll generate a title, description, and a suggested price for you to review before publishing."
    }]);
  };
  const toggleVehicle = (id: string, title: string) => {
    setSelectedVehicles(prev => prev.some(v => v.id === id) ? prev.filter(v => v.id !== id) : [...prev, {
      id,
      title
    }]);
  };
  // Attach selection state + handler to a listing before rendering
  const withSelection = (listing: ListingCardProps): ListingCardProps => ({
    ...listing,
    selected: selectedVehicles.some(v => v.id === listing.id),
    onToggleSelect: () => toggleVehicle(listing.id, listing.title)
  });
  const runQuery = (text: string) => {
    if (!text.trim() && selectedVehicles.length === 0) return;
    // If vehicles are selected, this is a specific question about them
    if (selectedVehicles.length > 0) {
      const vehicles = selectedVehicles;
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text || 'Tell me more about these',
        vehicles
      }]);
      setQuery('');
      setSelectedVehicles([]);
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        const names = vehicles.map(v => v.title).join(' and ');
        const answer = vehicles.length > 1 ? `Comparing ${names}: both are GCC-spec and well-maintained. The newer one holds resale value better and has lower mileage, while the other is more affordable upfront. For long-term ownership I'd lean toward the lower-mileage option; for budget, the cheaper one is the smarter buy.` : `About the ${names}: it's a GCC-spec unit with a clean history and fair pricing versus the market. Service records check out and the mileage is reasonable for the year. It's a solid, low-risk choice — happy to dig into anything specific like financing, inspection, or negotiation room.`;
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'vehicle_answer',
          content: answer,
          vehicles
        }]);
      }, 1400);
      return;
    }
    // Detect seller intent — route to a one-time listing or a Business Hub offer
    const sellIntent = detectSellIntent(text);
    if (sellIntent) {
      setChatHistory(prev => [...prev, {
        role: 'user',
        content: text
      }]);
      setQuery('');
      setIsSearching(true);
      setTimeout(() => {
        setIsSearching(false);
        if (sellIntent.tier === 'individual') {
          // One-time seller — stay in chat, create a simple listing
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'seller_simple',
            content: "Great — upload a few photos and I'll help create the listing. I'll just need a few details: make/model, year, mileage, condition, accident history, GCC specs, and your asking price. Then I'll generate a title, description, and a suggested price for you to review."
          }]);
        } else {
          // Business / enterprise — offer to create a Business Hub
          const intro = sellIntent.tier === 'enterprise' ? `It sounds like you operate a business with multiple listings and customer inquiries. I recommend creating a Business Hub to manage your ${BUSINESS_LABEL_INLINE[sellIntent.type]}, receive leads, track inquiries, manage team access, and get your own shareable shop link to send to customers.` : `It sounds like you're managing multiple ${BUSINESS_LABEL_INLINE[sellIntent.type]}. I can create a Business Hub for you to manage inventory, leads, messages, and analytics in one place. Would you like me to set it up?`;
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'seller_offer',
            content: intro,
            sellType: sellIntent.type
          }]);
        }
      }, 1400);
      return;
    }
    const firstSearch = !hasResults;
    const category = firstSearch ? detectCategory(text) : searchCategory;
    if (firstSearch) setSearchCategory(category);
    setChatHistory(prev => [...prev, {
      role: 'user',
      content: text
    }]);
    setQuery('');
    setIsSearching(true);
    setTimeout(() => {
      setIsSearching(false);
      if (firstSearch) {
        setHasResults(true);
        const searchContent = category === 'property' ? 'I found 18 properties that match. I prioritized location, proximity to schools, community quality, and trusted listings.' : 'I found 27 matching vehicles. I prioritized GCC specs, clean history, lower mileage, and trusted sellers.';
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'search',
          content: searchContent,
          category
        }]);
        // Follow-up narrowing offer ~1s later so the user notices it
        const narrowContent = category === 'property' ? "I can narrow this down — tell me your budget, preferred area, number of bedrooms, or which school matters most, and I'll refine these." : "I can narrow this down for you — just tell me your budget, preferred color, or any other detail, and I'll refine these matches.";
        setTimeout(() => {
          setChatHistory(prev => [...prev, {
            role: 'ai',
            kind: 'narrow_prompt',
            content: narrowContent
          }]);
        }, 1000);
      } else {
        setHasNarrowed(true);
        setChatHistory(prev => [...prev, {
          role: 'ai',
          kind: 'narrowed',
          category,
          content: "Perfect — based on what you told me, I've narrowed it down to just 3 strong matches that fit your preferences best."
        }]);
      }
    }, 1500);
  };
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    // Fold any attachments into the query context, then clear them
    const attachmentNote = attachments.length > 0 ? ` [Attached: ${attachments.join(', ')}]` : '';
    runQuery(`${query}${attachmentNote}`.trim());
    setAttachments([]);
    setIsRecording(false);
  };
  const handlePromptClick = (prompt: string) => {
    runQuery(prompt);
  };
  return <div className="app-fill-screen flex-1 flex flex-col h-[calc(100vh-57px)] lg:h-screen bg-white relative overflow-hidden" data-id="element-2530">
      {/* Hidden file pickers for attachments */}
      <input ref={imageInputRef} type="file" accept="image/*" multiple onChange={handleFilesSelected} className="hidden" data-id="element-2531" />
      <input ref={docInputRef} type="file" accept=".pdf,.doc,.docx,.csv,.xlsx,.txt" multiple onChange={handleFilesSelected} className="hidden" data-id="element-2532" />
      {/* Background Illustrations — premium, grand, floating line-art */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden z-0" data-id="element-2533">
        {/* Soft ambient glows */}
        <div className="absolute top-[18%] left-1/2 -translate-x-1/2 w-[900px] h-[640px] bg-ai-purple/[0.06] blur-[140px] rounded-full" data-id="element-2534" />
        <div className="absolute top-[8%] left-[12%] w-[420px] h-[420px] bg-primary-indigo/[0.05] blur-[130px] rounded-full" data-id="element-2535" />
        <div className="absolute bottom-[6%] right-[14%] w-[460px] h-[460px] bg-electric-cyan/[0.05] blur-[140px] rounded-full" data-id="element-2536" />

        {/* Elegant flowing dashed path weaving across the canvas */}
        <svg className="absolute inset-0 w-full h-full" preserveAspectRatio="none" viewBox="0 0 1440 900" aria-hidden="true" data-id="element-2537">
          <path d="M-40,250 C220,120 360,360 620,300 S980,140 1180,260 1460,380 1520,210" fill="none" stroke="url(#bgPathGrad)" strokeWidth="1.5" strokeDasharray="2 12" strokeLinecap="round" className="opacity-[0.18]" data-id="element-2538" />
          <path d="M-60,640 C260,560 420,760 700,660 S1060,520 1320,640 1520,700 1540,560" fill="none" stroke="url(#bgPathGrad)" strokeWidth="1.5" strokeDasharray="2 12" strokeLinecap="round" className="opacity-[0.14]" data-id="element-2539" />
          <defs data-id="element-2540">
            <linearGradient id="bgPathGrad" x1="0" y1="0" x2="1" y2="0" data-id="element-2541">
              <stop offset="0%" stopColor="#4338CA" data-id="element-2542" />
              <stop offset="50%" stopColor="#A855F7" data-id="element-2543" />
              <stop offset="100%" stopColor="#4338CA" data-id="element-2544" />
            </linearGradient>
          </defs>
        </svg>

        {/* Floating line-art icons — confident strokes, indigo/violet tint */}
        {[{
        Icon: CarIcon,
        cls: 'top-[14%] left-[13%] w-28 h-28 text-primary-indigo opacity-[0.07]',
        d: 0
      }, {
        Icon: BriefcaseIcon,
        cls: 'top-[20%] right-[16%] w-24 h-24 text-ai-purple opacity-[0.07]',
        d: 1.2
      }, {
        Icon: HomeIcon,
        cls: 'bottom-[26%] right-[9%] w-36 h-36 text-primary-indigo opacity-[0.06]',
        d: 0.6
      }, {
        Icon: WrenchIcon,
        cls: 'bottom-[18%] left-[22%] w-20 h-20 text-ai-purple opacity-[0.07]',
        d: 1.8
      }, {
        Icon: ShoppingBagIcon,
        cls: 'top-[44%] right-[4%] w-28 h-28 text-primary-indigo opacity-[0.06]',
        d: 0.9
      }, {
        Icon: TruckIcon,
        cls: 'top-[46%] left-[4%] w-28 h-28 text-ai-purple opacity-[0.06]',
        d: 1.5
      }, {
        Icon: GraduationCapIcon,
        cls: 'bottom-[10%] left-[11%] w-24 h-24 text-primary-indigo opacity-[0.07]',
        d: 0.3
      }, {
        Icon: PlaneIcon,
        cls: 'top-[11%] right-[32%] w-20 h-20 text-ai-purple opacity-[0.07]',
        d: 2.1
      }, {
        Icon: UserIcon,
        cls: 'bottom-[14%] right-[26%] w-24 h-24 text-primary-indigo opacity-[0.07]',
        d: 1
      }].map(({
        Icon,
        cls,
        d
      }, i) => <motion.div key={i} className={`absolute ${cls}`} animate={{
        y: [0, -14, 0]
      }} transition={{
        duration: 9 + i % 4,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: d
      }} data-id="element-2545">
            <Icon className="w-full h-full" strokeWidth={1.25} data-id="element-2546" />
          </motion.div>)}

        {/* Sparkle accents drifting along the path */}
        {[{
        cls: 'top-[28%] left-[40%] w-5 h-5 text-ai-purple opacity-[0.18]',
        d: 0.4
      }, {
        cls: 'top-[18%] left-[64%] w-4 h-4 text-primary-indigo opacity-[0.16]',
        d: 1.1
      }, {
        cls: 'bottom-[32%] left-[30%] w-4 h-4 text-primary-indigo opacity-[0.16]',
        d: 1.7
      }, {
        cls: 'bottom-[24%] right-[36%] w-5 h-5 text-ai-purple opacity-[0.18]',
        d: 0.8
      }].map((s, i) => <motion.div key={`sp-${i}`} className={`absolute ${s.cls}`} animate={{
        scale: [1, 1.25, 1],
        opacity: [0.12, 0.22, 0.12]
      }} transition={{
        duration: 4 + i,
        repeat: Infinity,
        ease: 'easeInOut',
        delay: s.d
      }} data-id="element-2547">
            <SparklesIcon className="w-full h-full" strokeWidth={1.5} data-id="element-2548" />
          </motion.div>)}
      </div>

      {/* Chat toolbar — always visible above the thread (never scrolls away) */}
      {(hasResults || isSearching || chatHistory.length > 0) && <div className="flex-shrink-0 px-4 lg:px-12 pt-4 pb-3 relative z-30 bg-white/60 backdrop-blur-md after:content-[''] after:absolute after:inset-x-0 after:top-full after:h-6 after:bg-gradient-to-b after:from-white/60 after:to-transparent after:pointer-events-none" data-id="element-2549">
          <div className="max-w-6xl mx-auto w-full" data-id="element-2550">
            <button onClick={resetConversation} className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-sm font-medium text-slate-700 shadow-sm hover:border-primary-indigo hover:text-primary-indigo transition-colors" data-id="element-2551">
              <PlusIcon className="w-4 h-4" data-id="element-2552" />
              New request
            </button>
          </div>
        </div>}

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto overflow-x-hidden px-4 lg:px-12 pb-24 lg:pb-8 pt-2 lg:pt-0 relative z-10" data-id="element-2553">
        <div className="max-w-6xl mx-auto w-full h-full flex flex-col min-w-0" data-id="element-2554">
          {!hasResults && !isSearching && chatHistory.length === 0 ?
        // Initial State (Hero)
        <div className="flex-1 flex flex-col items-center justify-center py-6" data-id="element-2555">
              <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="w-full max-w-6xl flex flex-col items-center relative z-10" data-id="element-2556">
                {/* Top Icon / Logo */}
                <div className="relative mb-4" data-id="element-2557">
                  <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 20,
                repeat: Infinity,
                ease: 'linear'
              }} className="absolute -inset-3.5 border border-slate-200 border-dashed rounded-full opacity-50" data-id="element-2558" />
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-white rounded-full flex items-center justify-center shadow-sm border border-slate-100 relative z-10 overflow-hidden" data-id="element-2559">
                    {isShopMode && shopLogo ? <img src={shopLogo} alt={shopName || 'Shop'} className="w-full h-full object-cover" data-id="element-2560" /> : <SparklesIcon className="w-8 h-8 lg:w-10 lg:h-10 text-primary-indigo" data-id="element-2561" />}
                  </div>
                </div>

                {/* Main Heading */}
                <div className="text-center space-y-2.5 mb-6" data-id="element-2562">
                  {isShopMode ? <>
                      <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-dark-navy tracking-tight" data-id="element-2563">
                        Welcome to{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-2564">
                          {shopName || 'Our Shop'}
                        </span>
                      </h1>
                      <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed" data-id="element-2565">
                        Ask our AI assistant anything about our inventory,
                        pricing, or services.
                      </p>
                    </> : <>
                      <h1 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-dark-navy tracking-tight" data-id="element-2566">
                        Ask Talab{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-indigo to-ai-purple" data-id="element-2567">
                          Anything
                        </span>
                      </h1>
                      <p className="text-slate-500 text-sm lg:text-base max-w-xl mx-auto leading-relaxed" data-id="element-2568">
                        Cars, properties, jobs, services, products and more.
                        <br data-id="element-2569" />
                        Talab understands your needs, compares options, and
                        helps you make better decisions.
                      </p>
                    </>}
                </div>

                {/* Category Ribbon — full width, evenly distributed, no scroll */}
                <div className="w-full max-w-5xl mb-6" data-id="element-2570">
                  <div className="flex items-stretch w-full p-1.5 bg-white rounded-2xl border border-slate-100 shadow-sm" data-id="element-2571">
                    {categoryRibbonItems.map((item, idx) => {
                  const Icon = item.icon;
                  return <Fragment key={item.label} data-id="element-2572">
                          <button onClick={() => {
                      setQuery(item.prompt + ' ');
                      textareaRef.current?.focus();
                    }} className="flex-1 min-w-0 flex flex-col items-center justify-center gap-1.5 px-1 py-2.5 rounded-xl text-slate-600 hover:bg-slate-50 transition-all group relative" data-id="element-2573">
                            <Icon className="w-5 h-5 text-primary-indigo group-hover:scale-110 transition-transform duration-300" data-id="element-2574" />
                            <span className="text-[10px] font-bold uppercase tracking-wider truncate max-w-full" data-id="element-2575">
                              {item.label}
                            </span>
                          </button>
                          {idx < categoryRibbonItems.length - 1 && <div className="w-px self-center h-7 bg-slate-100 flex-shrink-0" data-id="element-2576" />}
                        </Fragment>;
                })}
                  </div>
                </div>

                {/* AI Input Area */}
                <div className="w-full max-w-3xl bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] border border-slate-100 p-2 mb-8 relative group transition-all hover:shadow-[0_8px_30px_rgb(0,0,0,0.12)] z-10" data-id="element-2577">
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-3xl blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-2578" />
                  <div className="relative bg-white rounded-[22px] p-5 flex flex-col gap-3" data-id="element-2579">
                    <textarea ref={textareaRef} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit();
                  }
                }} placeholder={isShopMode ? `Ask ${shopName || 'us'} anything...` : 'Ask Talab anything...'} className="w-full resize-none outline-none text-base lg:text-lg text-slate-800 placeholder:text-slate-300 bg-transparent min-h-[44px]" rows={1} data-id="element-2580" />

                    {/* Attachment chips */}
                    {attachments.length > 0 && <div className="flex flex-wrap gap-2" data-id="element-2581">
                        {attachments.map((name, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-slate-50 border border-slate-100 text-slate-600 text-xs font-medium pl-3 pr-1.5 py-1.5 rounded-full" data-id="element-2582">
                            <PaperclipIcon className="w-3.5 h-3.5" data-id="element-2583" />
                            <span className="max-w-[140px] truncate" data-id="element-2584">
                              {name}
                            </span>
                            <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-slate-200 rounded-full" aria-label={`Remove ${name}`} data-id="element-2585">
                              <XIcon className="w-3.5 h-3.5" data-id="element-2586" />
                            </button>
                          </span>)}
                      </div>}

                    <div className="flex items-center justify-between mt-2" data-id="element-2587">
                      {/* Verb Row */}
                      <div className="flex items-center gap-3 text-[11px] font-semibold text-slate-400 uppercase tracking-widest hidden sm:flex" data-id="element-2588">
                        <span data-id="element-2589">Find</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-2590" />
                        <span data-id="element-2591">Buy</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-2592" />
                        <span data-id="element-2593">Sell</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-2594" />
                        <span data-id="element-2595">Hire</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-2596" />
                        <span data-id="element-2597">Rent</span>
                        <span className="w-1 h-1 rounded-full bg-slate-200" data-id="element-2598" />
                        <span data-id="element-2599">Compare</span>
                      </div>

                      <div className="flex items-center gap-1 ml-auto" data-id="element-2600">
                        <button type="button" onClick={toggleRecording} aria-label="Voice input" className={`p-2.5 rounded-full transition-colors ${isRecording ? 'text-white bg-error animate-pulse' : 'text-slate-400 hover:text-primary-indigo hover:bg-slate-50'}`} data-id="element-2601">
                          <MicIcon className="w-5 h-5" data-id="element-2602" />
                        </button>
                        <button type="button" onClick={() => imageInputRef.current?.click()} aria-label="Attach image" className="p-2.5 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors" data-id="element-2603">
                          <ImageIcon className="w-5 h-5" data-id="element-2604" />
                        </button>
                        <button type="button" onClick={() => docInputRef.current?.click()} aria-label="Attach file" className="p-2.5 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors" data-id="element-2605">
                          <PaperclipIcon className="w-5 h-5" data-id="element-2606" />
                        </button>
                        <button onClick={() => handleSubmit()} disabled={!query.trim() && attachments.length === 0} className="ml-2 p-3.5 bg-primary-indigo text-white rounded-full hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm" data-id="element-2607">
                          <ArrowUpIcon className="w-5 h-5" data-id="element-2608" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Example Prompts */}
                <div className="w-full max-w-5xl mb-8 z-10 relative" data-id="element-2609">
                  <div className="flex items-center justify-center gap-4 mb-5" data-id="element-2610">
                    <div className="h-px bg-slate-100 flex-1 max-w-[40px]" data-id="element-2611" />
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400" data-id="element-2612">
                      Try asking something like
                    </p>
                    <div className="h-px bg-slate-100 flex-1 max-w-[40px]" data-id="element-2613" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-2614">
                    {suggestedPrompts.map((prompt, index) => {
                  const Icon = prompt.icon;
                  return <motion.button key={index} initial={{
                    opacity: 0,
                    y: 10
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: 0.2 + index * 0.05
                  }} onClick={() => handlePromptClick(prompt.text)} className="flex items-center justify-between px-4 py-3 bg-white border border-slate-100 rounded-2xl hover:border-slate-200 hover:bg-slate-50/50 transition-all text-left group shadow-sm" data-id="element-2615">
                          <div className="flex items-center gap-3" data-id="element-2616">
                            <div className="p-2 bg-slate-50 rounded-lg group-hover:bg-white transition-colors" data-id="element-2617">
                              <Icon className="w-4 h-4 text-primary-indigo/60" data-id="element-2618" />
                            </div>
                            <span className="text-[13px] font-medium text-slate-600 leading-snug pr-2" data-id="element-2619">
                              {prompt.text}
                            </span>
                          </div>
                          <ChevronRightIcon className="w-4 h-4 text-slate-300 group-hover:text-primary-indigo/50 transition-colors flex-shrink-0" data-id="element-2620" />
                        </motion.button>;
                })}
                  </div>
                </div>
              </motion.div>
            </div> :
        // Chat History State
        <div className="space-y-6 pb-36 pt-2" data-id="element-2621">
              {chatHistory.map((msg, idx) => <div key={idx} data-id="element-2622">
                  {msg.role === 'user' ? <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="flex justify-end mb-6" data-id="element-2623">
                      <div className="bg-slate-100 rounded-2xl rounded-tr-sm px-5 py-3 max-w-[80%] shadow-sm" data-id="element-2624">
                        {msg.vehicles && msg.vehicles.length > 0 && <div className="flex flex-wrap gap-2 mb-3" data-id="element-2625">
                            {msg.vehicles.map(v => <span key={v.id} className="inline-flex items-center gap-1.5 bg-white border border-primary-indigo/20 text-primary-indigo text-xs font-medium px-3 py-1.5 rounded-full" data-id="element-2626">
                                <SparklesIcon className="w-3.5 h-3.5" data-id="element-2627" />
                                {v.title}
                              </span>)}
                          </div>}
                        <p className="text-slate-800 text-[15px]" data-id="element-2628">
                          {msg.content}
                        </p>
                      </div>
                    </motion.div> : <div className="mb-6" data-id="element-2629">
                      <AIResponse message={msg.content} insights={msg.kind === 'search' ? msg.category === 'property' ? propertyInsights : mockInsights : undefined} onInsightClick={scrollToListing} data-id="element-2630" />

                      {msg.kind === 'seller_offer' && <div className="flex flex-col sm:flex-row gap-3 mt-4 ml-0 md:ml-14" data-id="element-2631">
                          <button onClick={() => createBusinessHub(msg.sellType ?? 'generic')} className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-gradient-to-r from-primary-indigo to-ai-purple text-white rounded-xl font-semibold shadow-sm hover:brightness-110 transition-all" data-id="element-2632">
                            <BriefcaseIcon className="w-5 h-5" data-id="element-2633" />
                            Yes, create Business Hub
                          </button>
                          <button onClick={continueWithOneListing} className="inline-flex items-center justify-center px-5 py-3 bg-white border border-slate-200 text-slate-700 rounded-xl font-medium hover:border-primary-indigo hover:text-primary-indigo transition-colors" data-id="element-2634">
                            Continue with one listing
                          </button>
                        </div>}

                      {msg.kind === 'narrowed' ?
              // Narrowed: 3 strong matches
              <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.4
              }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-5 ml-0 md:ml-12" data-id="element-2635">
                          {(msg.category === 'property' ? propertyNarrowed : narrowedListings).map((listing, lIdx) => <motion.div key={listing.id} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  delay: 0.5 + lIdx * 0.1
                }} data-id="element-2636">
                              <ListingCard {...withSelection(listing)} data-id="element-2637" />
                            </motion.div>)}
                        </motion.div> : msg.kind === 'search' ?
              // Broad search: swipeable carousel
              <motion.div initial={{
                opacity: 0
              }} animate={{
                opacity: 1
              }} transition={{
                delay: 0.4
              }} className="mt-6 ml-0 md:ml-14" data-id="element-2638">
                          <div className="flex items-center justify-between mb-3 pr-4" data-id="element-2639">
                            <span className="text-sm font-medium text-slate-500" data-id="element-2640">
                              {msg.category === 'property' ? 'Showing top matches of 18' : 'Showing top matches of 27'}
                            </span>
                            <span className="text-xs text-slate-400 hidden md:inline" data-id="element-2641">
                              {msg.category === 'property' ? 'Tap "Ask AI" on a home to ask about it →' : 'Tap "Ask AI" on a car to ask about it →'}
                            </span>
                          </div>
                          <ListingCarousel listings={(msg.category === 'property' ? propertyListings : mockListings).map(withSelection)} data-id="element-2642" />
                        </motion.div> : null}
                    </div>}
                </div>)}

              {isSearching && <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="flex gap-4" data-id="element-2643">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gradient-to-br from-primary-indigo to-ai-purple flex items-center justify-center shadow-md" data-id="element-2644">
                    <SparklesIcon className="w-5 h-5 text-white animate-pulse" data-id="element-2645" />
                  </div>
                  <div className="bg-white rounded-2xl rounded-tl-none p-5 shadow-soft border border-slate-100 flex items-center gap-2" data-id="element-2646">
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-2647" />
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-2648" />
                    <div className="w-2 h-2 bg-primary-indigo rounded-full animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-2649" />
                  </div>
                </motion.div>}
              {/* Scroll anchor — keeps newest message + input in view */}
              <div ref={bottomRef} className="h-px" data-id="element-2650" />
            </div>}
        </div>
      </div>

      {/* Floating Input (always visible while chatting) */}
      {(hasResults || isSearching || chatHistory.length > 0) && <>
          {/* Soft fade so content dissolves behind the floating pill */}
          <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none z-20" data-id="element-2651" />
          <div className="absolute bottom-5 left-0 right-0 px-4 lg:px-8 z-30 pointer-events-none flex justify-center" data-id="element-2652">
            <div className="w-full max-w-3xl pointer-events-auto" data-id="element-2653">
              {/* Selected vehicle tags */}
              {selectedVehicles.length > 0 && <motion.div initial={{
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex flex-wrap items-center gap-2 mb-3 px-1" data-id="element-2654">
                  <span className="text-xs font-medium text-slate-500 mr-1" data-id="element-2655">
                    Asking about:
                  </span>
                  {selectedVehicles.map(v => <span key={v.id} className="inline-flex items-center gap-1.5 bg-white border border-primary-indigo/30 text-primary-indigo text-xs font-medium pl-3 pr-2 py-1.5 rounded-full shadow-sm" data-id="element-2656">
                      <SparklesIcon className="w-3.5 h-3.5" data-id="element-2657" />
                      {v.title}
                      <button onClick={() => toggleVehicle(v.id, v.title)} aria-label={`Remove ${v.title}`} className="ml-0.5 p-0.5 hover:bg-primary-indigo/10 rounded-full transition-colors" data-id="element-2658">
                        <XIcon className="w-3.5 h-3.5" data-id="element-2659" />
                      </button>
                    </span>)}
                </motion.div>}
              {/* Attachment chips */}
              {attachments.length > 0 && <div className="flex flex-wrap items-center gap-2 mb-3 px-1" data-id="element-2660">
                  {attachments.map((name, i) => <span key={i} className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 text-xs font-medium pl-3 pr-1.5 py-1.5 rounded-full shadow-sm" data-id="element-2661">
                      <PaperclipIcon className="w-3.5 h-3.5" data-id="element-2662" />
                      <span className="max-w-[140px] truncate" data-id="element-2663">{name}</span>
                      <button onClick={() => removeAttachment(i)} className="p-0.5 hover:bg-slate-100 rounded-full" aria-label={`Remove ${name}`} data-id="element-2664">
                        <XIcon className="w-3.5 h-3.5" data-id="element-2665" />
                      </button>
                    </span>)}
                </div>}
              <div className="w-full bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] border border-slate-100 p-2 relative group transition-all" data-id="element-2666">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-indigo/20 to-ai-purple/20 rounded-3xl blur opacity-0 group-focus-within:opacity-100 transition duration-500" data-id="element-2667" />
                <div className="relative bg-white rounded-3xl flex items-end gap-2 px-4 py-2" data-id="element-2668">
                  <button type="button" onClick={() => docInputRef.current?.click()} aria-label="Attach file" className="p-2 text-slate-400 hover:text-primary-indigo hover:bg-slate-50 rounded-full transition-colors flex-shrink-0 mb-0.5" data-id="element-2669">
                    <PaperclipIcon className="w-5 h-5" data-id="element-2670" />
                  </button>
                  <textarea value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit();
                }
              }} rows={1} placeholder={selectedVehicles.length > 0 ? `Ask about ${selectedVehicles.length > 1 ? 'these vehicles' : 'this vehicle'}...` : 'Ask Talab anything...'} className="flex-1 bg-transparent outline-none text-slate-800 placeholder:text-slate-400 resize-none py-2 max-h-32 leading-relaxed" data-id="element-2671" />
                  <button type="button" onClick={toggleRecording} aria-label="Voice input" className={`p-2 rounded-full transition-colors flex-shrink-0 mb-0.5 ${isRecording ? 'text-white bg-error animate-pulse' : 'text-slate-400 hover:text-primary-indigo hover:bg-slate-50'}`} data-id="element-2672">
                    <MicIcon className="w-5 h-5" data-id="element-2673" />
                  </button>
                  <button onClick={() => handleSubmit()} disabled={!query.trim() && selectedVehicles.length === 0 && attachments.length === 0 || isSearching} className="p-2.5 bg-primary-indigo text-white rounded-full hover:bg-primary-indigo/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm flex-shrink-0 mb-0.5" data-id="element-2674">
                    <ArrowUpIcon className="w-5 h-5" data-id="element-2675" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>}
    </div>;
}